import { Form, FormCancel } from "../components";
import { useFeedback } from "../hooks/useFeedback";
import PasswordInput from '../components/PasswordInput';
import Icon from "../components/Icon";
import { Toast, PageTitle, EmptyState, Card, Btn, ConfirmModal, Input, Modal, Select, Spinner } from "../components";
import { passwordValidationError } from '../utils/passwordPolicy';
import { useState, useEffect } from "react";
import { apiFetch } from "../api";
import { DEFAULT_TIMEZONE, resolveTimezoneOptions } from "../constants/timezones";

const formatPlanOptionLabel = (plan) => {
  const summary = Array.isArray(plan.feature_summary) && plan.feature_summary.length
    ? plan.feature_summary.join(", ")
    : null;
  return summary ? `${plan.name || plan.code} — ${summary}` : (plan.name || plan.code);
};

function formatPhone(digits) {
  const d = (digits || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function phoneToDisplay(stored) {
  const d = (stored || "").replace(/\D/g, "");
  const local = d.startsWith("55") ? d.slice(2) : d;
  return formatPhone(local);
}

function phoneToStore(displayValue) {
  const d = (displayValue || "").replace(/\D/g, "").slice(0, 11);
  return d ? `55${d}` : "";
}

const emptyForm = () => ({
  nome: "",
  cnpj: "",
  telefone: "",
  email: "",
  plan_code: "professional",
  subscription_status: "active",
  display_name: "",
  motorista_base_label: "",
  motorista_base_endereco: "",
  pedido_importado_label: "",
  pedido_manual_label: "",
  documento_pedido_label: "",
  documento_operacional_label: "",
  integracao_nome: "",
  embraer_sgt_habilitado: false,
  validar_sgt_no_disparo: false,
  evolution_instance: "",
  central_whatsapp: "",
  notificacao_provider: "",
  meta_phone_number_id: "",
  estado: "",
  timezone: DEFAULT_TIMEZONE,
  webhook_mapa_url: "",
  webhook_assinatura_url: "",
  webhook_rota_atualizacao_url: "",
  privacy_policy_url: "",
  admin_nome: "",
  admin_email: "",
  admin_senha: "",
  geography_cidades_text: "",
  geography_cidade_com_regiao: "",
  geography_regioes_text: "",
});

const splitLines = (value) => String(value || "")
  .split(/\r?\n/)
  .map((item) => item.trim())
  .filter(Boolean);

const parseRegioesText = (value) => {
  const regioes = {};
  for (const line of splitLines(value)) {
    const separatorIndex = line.indexOf(":");
    if (separatorIndex <= 0) continue;
    const nome = line.slice(0, separatorIndex).trim();
    const bairros = line.slice(separatorIndex + 1)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    if (nome && bairros.length) regioes[nome] = bairros;
  }
  return regioes;
};

const formatRegioesText = (regioes = {}) => (
  Object.entries(regioes || {})
    .map(([regiao, bairros]) => `${regiao}: ${(bairros || []).join(", ")}`)
    .join("\n")
);

const buildGeographyConfig = (form) => {
  const cidades = splitLines(form.geography_cidades_text);
  const cidadeComRegiao = String(form.geography_cidade_com_regiao || "").trim();
  const regioes = parseRegioesText(form.geography_regioes_text);
  const hasGeography = cidades.length || cidadeComRegiao || Object.keys(regioes).length;
  if (!hasGeography) return undefined;
  return {
    cidades,
    cidade_com_regiao: cidadeComRegiao,
    regioes_bairros: regioes,
  };
};

export default function ClientesRotaflexPage() {
  const [clientes, setClientes] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitError, setSubmitError] = useState("");
  const { toast, toastType, showToast, showError, dismissToast } = useFeedback("clientes_rotaflex");
  const [confirmId, setConfirmId] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [loadingReadiness, setLoadingReadiness] = useState(false);
  const [planOptions, setPlanOptions] = useState([
    { code: "basic", name: "Básico" },
    { code: "professional", name: "Profissional" },
    { code: "enterprise", name: "Enterprise" },
  ]);

  useEffect(() => {
    setLoading(true);
    setLoadError("");
    Promise.all([
      apiFetch("/clientes-rotaflex"),
      apiFetch("/plans?all=1").catch(() => []),
    ])
      .then(([clientes, plans]) => {
        setClientes(clientes);
        if (Array.isArray(plans) && plans.length) setPlanOptions(plans);
      })
      .catch(e => { setLoadError(e.message); showError(e.message); })
      .finally(() => setLoading(false));
  }, [refresh, showError]);



  const openNew = () => {
    setSubmitError("");
    setForm(emptyForm());
    setEditItem(null);
    setModal(true);
  };

  const openEdit = async (c) => {
    setSubmitError("");
    const config = c.config || {};
    const geography = config.geography || {};
    const webhooks = config.webhooks || {};
    setForm({
      nome: c.nome,
      cnpj: c.cnpj || "",
      telefone: c.telefone || "",
      email: c.email || "",
      plan_code: c.plan_code || "professional",
      subscription_status: c.subscription_status || "active",
      display_name: config.display_name || c.nome || "",
      motorista_base_label: config.motorista_base_label || "",
      motorista_base_endereco: config.motorista_base_endereco || "",
      pedido_importado_label: config.pedido_importado_label || "",
      pedido_manual_label: config.pedido_manual_label || "",
      documento_pedido_label: config.documento_pedido_label || "",
      documento_operacional_label: config.documento_operacional_label || "",
      integracao_nome: config.integracao_nome || "",
      embraer_sgt_habilitado: c.embraer_sgt_habilitado === true,
      validar_sgt_no_disparo: Boolean(config.validar_sgt_no_disparo),
      evolution_instance: config.evolution_instance || "",
      central_whatsapp: config.central_whatsapp || config.centralWhatsapp || "",
      notificacao_provider: config.notificacao_provider || config.notificacaoProvider || "",
      meta_phone_number_id: config.meta_phone_number_id || config.meta?.phone_number_id || "",
      estado: config.estado || "",
      timezone: config.timezone || DEFAULT_TIMEZONE,
      webhook_mapa_url: webhooks.mapa || config.webhook_mapa_url || "",
      webhook_assinatura_url: webhooks.assinatura || config.webhook_assinatura_url || "",
      webhook_rota_atualizacao_url: webhooks.rota_atualizacao || webhooks.rotaAtualizacao || config.webhook_rota_atualizacao_url || "",
      privacy_policy_url: config.privacy_policy_url || config.privacyPolicyUrl || "",
      geography_cidades_text: Array.isArray(geography.cidades) ? geography.cidades.join("\n") : "",
      geography_cidade_com_regiao: Object.prototype.hasOwnProperty.call(geography, "cidade_com_regiao") ? geography.cidade_com_regiao || "" : "",
      geography_regioes_text: formatRegioesText(geography.regioes_bairros),
    });
    setEditItem(c.id);
    setModal(true);
    setReadiness(null);
    setLoadingReadiness(true);
    try {
      const payload = await apiFetch(`/clientes-rotaflex/${c.id}/readiness`);
      setReadiness(payload);
    } catch (e) {
      showError(e.message);
    } finally {
      setLoadingReadiness(false);
    }
  };

  const save = async () => {
    if (saving) return;
    setSubmitError("");
    if (!form.nome) { showError("Nome é obrigatório."); return; }
    if (!editItem) {
      if (!form.admin_nome || !form.admin_email || !form.admin_senha) {
        showError("Admin inicial: nome, email e senha são obrigatórios.");
        return;
      }
    }
    if (!editItem && passwordValidationError(form.admin_senha)) {
      setSubmitError(passwordValidationError(form.admin_senha));
      return;
    }
    const geographyConfig = buildGeographyConfig(form);
    if (!editItem) {
      const missing = [];
      if (!form.motorista_base_endereco.trim()) missing.push("endereço da opção 1");
      if (form.notificacao_provider !== "meta_cloud" && !form.evolution_instance.trim()) {
        missing.push("instância Evolution API");
      }
      if (!form.estado.trim()) missing.push("estado padrão dos endereços");
      if (!form.timezone.trim()) missing.push("fuso horário");
      if (!form.webhook_mapa_url.trim()) missing.push("webhook mapa n8n");
      if (!form.webhook_assinatura_url.trim()) missing.push("webhook assinatura n8n");
      if (!form.webhook_rota_atualizacao_url.trim()) missing.push("webhook atualização de rota n8n");
      if (!geographyConfig?.cidades?.length) missing.push("cidades atendidas");
      if (missing.length) {
        showError(`Configuração operacional obrigatória: ${missing.join(", ")}.`);
        return;
      }
    }
    const payload = {
      ...form,
      config: {
        display_name: form.display_name || form.nome,
        motorista_base_label: form.motorista_base_label,
        motorista_base_endereco: form.motorista_base_endereco,
        pedido_importado_label: form.pedido_importado_label,
        pedido_manual_label: form.pedido_manual_label,
        documento_pedido_label: form.documento_pedido_label,
        documento_operacional_label: form.documento_operacional_label,
        integracao_nome: form.integracao_nome,
        embraer_sgt_habilitado: form.embraer_sgt_habilitado,
        validar_sgt_no_disparo: form.validar_sgt_no_disparo,
        evolution_instance: form.evolution_instance,
        central_whatsapp: form.central_whatsapp,
        notificacao_provider: form.notificacao_provider,
        meta_phone_number_id: form.meta_phone_number_id,
        estado: form.estado,
        timezone: form.timezone,
        webhooks: {
          mapa: form.webhook_mapa_url,
          assinatura: form.webhook_assinatura_url,
          rota_atualizacao: form.webhook_rota_atualizacao_url,
        },
        privacy_policy_url: form.privacy_policy_url || undefined,
        ...(geographyConfig ? { geography: geographyConfig } : {}),
      },
    };
    setSaving(true);
    try {
      if (editItem) {
        const res = await apiFetch(`/clientes-rotaflex/${editItem}`, { method: "PUT", body: JSON.stringify(payload) });
        setClientes(clientes.map(c => c.id === editItem ? { ...c, ...res } : c));
        showToast("Cliente atualizado.");
      } else {
        const onboardPayload = {
          nome: form.nome,
          cnpj: form.cnpj,
          telefone: form.telefone,
          email: form.email,
          plan_code: form.plan_code,
          subscription_status: form.subscription_status,
          config: payload.config,
          admin: {
            nome: form.admin_nome,
            email: form.admin_email,
            senha: form.admin_senha,
          },
        };
        const res = await apiFetch("/clientes-rotaflex/onboard", { method: "POST", body: JSON.stringify(onboardPayload) });
        const cliente = res.cliente || res;
        setClientes([...clientes, { ...cliente, total_usuarios: 1 }]);
        showToast("Transportadora e administrador inicial cadastrados.");
      }
      setModal(false);
    } catch (e) { setSubmitError(e.message); showError(e.message); }
    finally { setSaving(false); }
  };

  const desativar = async () => {
    try {
      await apiFetch(`/clientes-rotaflex/${confirmId}`, { method: "DELETE" });
      setClientes(clientes.map(c => c.id === confirmId ? { ...c, ativo: false } : c));
      setConfirmId(null);
      showToast("Cliente desativado.");
    } catch (e) { showError(e.message); }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString("pt-BR");

  return (
    <div className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        <div>
          <PageTitle icon="building">Clientes RotaFlex</PageTitle>
          <p style={{ color: "var(--muted)", fontSize: '1rem', marginTop: 2 }}>
            Transportadoras que contratam o RotaFlex · {clientes.filter(c => c.ativo).length} ativas
          </p>
        </div>
        <Btn onClick={openNew}>+ Novo cliente</Btn>
      </div>

      {loadError && <Card style={{ marginBottom: 16 }}><p role="alert" style={{ color: "var(--danger)", marginBottom: 12 }}>{loadError}</p><Btn variant="secondary" onClick={() => setRefresh(value => value + 1)}>Tentar novamente</Btn></Card>}

      <Card style={{ padding: 0, overflowX: "auto" }}>
        <table style={{ width: "100%", minWidth: 940, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              {["Empresa", "CNPJ", "Contato", "Plano", "Usuários", "Desde", "Status", ""].map(h => (
                <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
	              <tr><td colSpan={8} style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}><Spinner /></td></tr>
            )}
            {!loading && !loadError && clientes.length === 0 && (
	              <tr><td colSpan={8} style={{ padding: 40, textAlign: "center" }}>
                <EmptyState title="Nenhum cliente cadastrado ainda" icon="building"><p>Cadastre uma transportadora para organizar seus usuários e sua operação.</p></EmptyState>
                <Btn size="sm" onClick={openNew}>+ Novo cliente</Btn>
              </td></tr>
            )}
            {!loading && clientes.map((c, i) => (
              <tr key={c.id}
                style={{ borderBottom: i < clientes.length - 1 ? "1px solid var(--border)" : "none", transition: "background 0.15s", opacity: c.ativo ? 1 : 0.5 }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--s2)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "12px 16px", fontWeight: 600 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 34, height: 34, background: "rgba(249,115,22,0.12)", border: "1px solid rgba(249,115,22,0.2)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: '1rem', flexShrink: 0 }}><Icon name="building" /></div>
                    {c.nome}
                  </div>
                </td>
                <td style={{ padding: "12px 16px", color: "var(--muted)", fontSize: '0.9375rem', fontFamily: "var(--font-mono)" }}>{c.cnpj || "—"}</td>
	                <td style={{ padding: "12px 16px", fontSize: '0.9375rem' }}>
                  {c.email && <div style={{ color: "var(--text)" }}>{c.email}</div>}
                  {c.telefone && <div style={{ color: "var(--muted)", fontSize: '0.875rem' }}>{c.telefone}</div>}
                  {!c.email && !c.telefone && <span style={{ color: "var(--muted)" }}>—</span>}
	                </td>
	                <td style={{ padding: "12px 16px", fontSize: '0.9375rem' }}>
	                  <div style={{ fontWeight: 600 }}>{c.plan_name || c.plan_code || "—"}</div>
	                  <div style={{ color: "var(--muted)", fontSize: '0.875rem' }}>{c.subscription_status || "sem assinatura"}</div>
	                </td>
	                <td style={{ padding: "12px 16px" }}>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: '0.9375rem', color: "var(--accent)", fontWeight: 600 }}>
                    {c.total_usuarios ?? 0}
                  </span>
                </td>
                <td style={{ padding: "12px 16px", color: "var(--muted)", fontSize: '0.9375rem' }}>{formatDate(c.criado_em)}</td>
                <td style={{ padding: "12px 16px" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 9999, fontSize: '0.8125rem', fontWeight: 600, background: c.ativo ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)", color: c.ativo ? "#22c55e" : "#ef4444" }}>
                    {c.ativo ? "Ativo" : "Inativo"}
                  </span>
                </td>
                <td style={{ padding: "12px 16px" }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Btn size="sm" variant="secondary" onClick={() => openEdit(c)}>Editar</Btn>
                    {c.ativo && <Btn size="sm" variant="danger" onClick={() => setConfirmId(c.id)}>Desativar</Btn>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {modal && (
        <Modal title={editItem ? "Editar cliente" : "Novo cliente"} onClose={() => !saving && setModal(false)} maxWidth={760}>
          <Form values={form} onSubmit={save} busy={saving} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {editItem && (
              <div style={{ background: "var(--s2)", border: "1px solid var(--border)", borderRadius: 10, padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 10 }}>
                  <div style={{ fontSize: '0.9375rem', fontWeight: 700 }}>Configuração da transportadora</div>
                  {loadingReadiness ? (
                    <span style={{ fontSize: '0.875rem', color: "var(--muted)" }}>Avaliando...</span>
                  ) : readiness ? (
                    <span style={{
                      fontSize: '0.8125rem',
                      fontWeight: 700,
                      padding: "2px 8px",
                      borderRadius: 999,
                      background: readiness.ready_for_assisted_ops ? "rgba(34,197,94,0.15)" : "rgba(234,179,8,0.15)",
                      color: readiness.ready_for_assisted_ops ? "#22c55e" : "#eab308",
                    }}>
                      {readiness.passed_checks}/{readiness.total_checks} itens
                    </span>
                  ) : null}
                </div>
                {!loadingReadiness && readiness && (
                  <div style={{ display: "grid", gap: 6 }}>
                    {readiness.items.map((item) => (
                      <div key={item.id} style={{ fontSize: '0.875rem', lineHeight: 1.45, color: item.ok ? "var(--text)" : "#eab308" }}>
                        <Icon name={item.ok ? "check" : "warning"} size={15} /> {item.label}
                        {!item.ok && item.detail ? (
                          <div style={{ color: "var(--muted)", marginLeft: 22, marginTop: 2 }}>{item.detail}</div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <h4 className="rf-form-group-title">Identificação da transportadora</h4>
            <Input
              label="Nome da empresa *"
              value={form.nome}
              onChange={e => setForm({ ...form, nome: e.target.value })}
              placeholder="Transportes Silva"
            />
            <Input
              label="CNPJ"
              value={form.cnpj}
              onChange={e => setForm({ ...form, cnpj: e.target.value })}
              placeholder="00.000.000/0001-00"
            />
            <Input
              label="E-mail de contato"
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="contato@empresa.com"
            />
	            <Input
	              label="Telefone"
	              value={phoneToDisplay(form.telefone)}
	              onChange={e => setForm({ ...form, telefone: phoneToStore(e.target.value) })}
	              placeholder="(92) 99999-0000"
	            />
	            <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: '0.9375rem', color: "var(--muted)", fontWeight: 600 }}>
	              Plano
	              <select
	                value={form.plan_code}
	                onChange={e => setForm({ ...form, plan_code: e.target.value })}
	                style={{ height: 40, borderRadius: 8, border: "1px solid var(--border)", background: "var(--s2)", color: "var(--text)", padding: "0 12px" }}
	              >
	                {planOptions.map((plan) => (
	                  <option key={plan.code} value={plan.code}>{formatPlanOptionLabel(plan)}</option>
	                ))}
	              </select>
	            </label>
		            <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: '0.9375rem', color: "var(--muted)", fontWeight: 600 }}>
		              Status da assinatura
	              <select
	                value={form.subscription_status}
	                onChange={e => setForm({ ...form, subscription_status: e.target.value })}
	                style={{ height: 40, borderRadius: 8, border: "1px solid var(--border)", background: "var(--s2)", color: "var(--text)", padding: "0 12px" }}
	              >
	                <option value="trialing">Trial</option>
	                <option value="active">Ativa</option>
	                <option value="past_due">Pagamento pendente</option>
	                <option value="blocked">Bloqueada</option>
		                <option value="canceled">Cancelada</option>
		              </select>
		            </label>
		            {!editItem && (
		              <div style={{ display: "grid", gap: 10, paddingTop: 4, borderTop: "1px solid var(--border)" }}>
		                <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: "var(--text)" }}>Admin inicial</div>
		                <Input
		                  label="Nome do admin *"
		                  value={form.admin_nome}
		                  onChange={e => setForm({ ...form, admin_nome: e.target.value })}
		                  placeholder="Maria Silva"
		                />
		                <Input
		                  label="Email do admin *"
		                  type="email"
		                  value={form.admin_email}
		                  onChange={e => setForm({ ...form, admin_email: e.target.value })}
		                  placeholder="admin@empresa.com"
		                />
		                <PasswordInput
		                  label="Senha inicial *"
		                  value={form.admin_senha}
		                  onChange={e => setForm({ ...form, admin_senha: e.target.value })}
		                  placeholder="Mínimo 8 caracteres" minLength={8}
		                />
		              </div>
		            )}
		            <div style={{ display: "grid", gap: 10, paddingTop: 4, borderTop: "1px solid var(--border)" }}>
		              <p style={{ margin: 0, fontSize: '0.875rem', color: "var(--muted)", lineHeight: 1.5 }}>
		                Endereços de destino das rotas são cadastrados em <strong>Empresas de destino</strong> pelo admin do cliente.
		              </p>
		              <h4 className="rf-form-group-title">Operação e comunicação</h4>
		              <Input
		                label="Nome nas mensagens"
		                value={form.display_name}
		                onChange={e => setForm({ ...form, display_name: e.target.value })}
		                placeholder="Transportes Silva"
		              />
		              <Input
		                label="Opção 1 do motorista"
		                value={form.motorista_base_label}
		                onChange={e => setForm({ ...form, motorista_base_label: e.target.value })}
		                placeholder="Base"
		              />
		              <Input
		                label="Endereço da opção 1"
		                value={form.motorista_base_endereco}
		                onChange={e => setForm({ ...form, motorista_base_endereco: e.target.value })}
		                placeholder="Av. Djalma Batista, 1000, Manaus AM"
		              />
		              <div style={{ display: "grid", gap: 10, paddingTop: 4, borderTop: "1px dashed var(--border)" }}>
		                <p style={{ margin: 0, fontSize: '0.875rem', color: "var(--muted)", lineHeight: 1.5 }}>
		                  Rótulos exibidos no app para pedidos e documentos. Deixe em branco para usar os padrões do sistema.
		                </p>
		                <Input
		                  label="Pedido importado"
		                  value={form.pedido_importado_label}
		                  onChange={e => setForm({ ...form, pedido_importado_label: e.target.value })}
		                  placeholder="Pedido importado"
		                />
		                <Input
		                  label="Pedido manual"
		                  value={form.pedido_manual_label}
		                  onChange={e => setForm({ ...form, pedido_manual_label: e.target.value })}
		                  placeholder="Pedido manual"
		                />
		                <Input
		                  label="Nome da integração"
		                  value={form.integracao_nome}
		                  onChange={e => setForm({ ...form, integracao_nome: e.target.value })}
		                  placeholder="Embraer"
		                />
                <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200 sm:col-span-2">
                  <input type="checkbox" className="mt-1"
                    checked={form.embraer_sgt_habilitado}
                    onChange={e => setForm({ ...form, embraer_sgt_habilitado: e.target.checked })}
                  />
                  <span>Autorizar integração Embraer (SGT e planilha)
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      Apenas para transportadoras autorizadas a usar a conta Embraer configurada nesta instalação.
                    </span>
                  </span>
                </label>
		                <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200 sm:col-span-2">
		                  <input
		                    type="checkbox"
		                    className="mt-1"
		                    checked={form.validar_sgt_no_disparo}
		                    onChange={e => setForm({ ...form, validar_sgt_no_disparo: e.target.checked })}
		                  />
		                  <span>
		                    Validar pedidos no SGT antes do disparo (Embraer)
		                    <span className="block text-xs text-gray-500 dark:text-gray-400">
		                      Confere passageiros, trecho, horário e data contra a busca automática do SGT (6h/18h) ao disparar cada van.
		                    </span>
		                  </span>
		                </label>
		                <Input
		                  label="Documento do pedido"
		                  value={form.documento_pedido_label}
		                  onChange={e => setForm({ ...form, documento_pedido_label: e.target.value })}
		                  placeholder="Documento do pedido"
		                />
		                <Input
		                  label="Documento da van"
		                  value={form.documento_operacional_label}
		                  onChange={e => setForm({ ...form, documento_operacional_label: e.target.value })}
		                  placeholder="Documento da van"
		                />
		              </div>
				              <h4 className="rf-form-group-title">Canal de mensagens</h4>
				              <Select
				                label="Provedor de notificação WhatsApp"
			                value={form.notificacao_provider || ""}
			                onChange={e => setForm({ ...form, notificacao_provider: e.target.value })}
		              >
			                <option value="">Legado (n8n / Evolution)</option>
				                <option value="meta_cloud">Meta Cloud API (oficial)</option>
				              </Select>
		              <Input
		                label={form.notificacao_provider === "meta_cloud"
		                  ? "Instância Evolution API (não utilizada pela Meta)"
		                  : "Instância Evolution API *"}
		                value={form.evolution_instance}
		                onChange={e => setForm({ ...form, evolution_instance: e.target.value })}
		                placeholder={form.notificacao_provider === "meta_cloud" ? "Não se aplica" : "pereira"}
		                disabled={form.notificacao_provider === "meta_cloud"}
		              />
		              <Input
		                label="WhatsApp da central (suporte)"
		                value={form.central_whatsapp}
		                onChange={e => setForm({ ...form, central_whatsapp: e.target.value })}
		                placeholder="(16) 99975-0932"
		              />
		              <Input
		                label="ID do número de WhatsApp na Meta"
		                value={form.meta_phone_number_id}
		                onChange={e => setForm({ ...form, meta_phone_number_id: e.target.value.replace(/\D/g, "") })}
		                hint="Pode ser cadastrado antes de ativar o envio pela Meta."
		              />
		              <h4 className="rf-form-group-title">Localização e fuso horário</h4>
		              <Input
		                label="Estado padrão dos endereços"
		                value={form.estado}
		                onChange={e => setForm({ ...form, estado: e.target.value.toUpperCase().slice(0, 2) })}
		                placeholder="AM"
		              />
              <Select
                label="Fuso horário"
                value={form.timezone || DEFAULT_TIMEZONE}
                onChange={e => setForm({ ...form, timezone: e.target.value })}
              >
                {resolveTimezoneOptions(form.timezone).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
		              <h4 className="rf-form-group-title">Integrações e privacidade</h4>
		              <Input
		                label={`Webhook mapa n8n${!editItem ? " *" : ""}`}
                        required={!editItem}
		                value={form.webhook_mapa_url}
		                onChange={e => setForm({ ...form, webhook_mapa_url: e.target.value })}
		                placeholder="https://n8n.seudominio.com/webhook/mapa"
		              />
		              <Input
		                label={`Webhook assinatura n8n${!editItem ? " *" : ""}`}
                        required={!editItem}
		                value={form.webhook_assinatura_url}
		                onChange={e => setForm({ ...form, webhook_assinatura_url: e.target.value })}
		                placeholder="https://n8n.seudominio.com/webhook/eta"
		              />
		              <Input
		                label={`Webhook atualização de rota n8n${!editItem ? " *" : ""}`}
                        required={!editItem}
		                value={form.webhook_rota_atualizacao_url}
		                onChange={e => setForm({ ...form, webhook_rota_atualizacao_url: e.target.value })}
		                placeholder="https://n8n.seudominio.com/webhook/rota-atualizacao"
		              />
		              <Input
		                label="URL política de privacidade (opcional)"
		                value={form.privacy_policy_url}
		                onChange={e => setForm({ ...form, privacy_policy_url: e.target.value })}
		                placeholder="https://suaempresa.com/privacidade"
		              />
		              <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: '0.9375rem', color: "var(--muted)", fontWeight: 600 }}>
		                Cidades atendidas
		                <textarea
		                  value={form.geography_cidades_text}
		                  onChange={e => setForm({ ...form, geography_cidades_text: e.target.value })}
		                  placeholder={"Manaus\nIranduba"}
		                  rows={3}
		                  style={{ borderRadius: 8, border: "1px solid var(--border)", background: "var(--s2)", color: "var(--text)", padding: "10px 12px", resize: "vertical", font: "inherit", fontSize: '1rem' }}
		                />
		              </label>
		              <Input
		                label="Cidade com regiões"
		                value={form.geography_cidade_com_regiao}
		                onChange={e => setForm({ ...form, geography_cidade_com_regiao: e.target.value })}
		                placeholder="Manaus"
		              />
		              <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: '0.9375rem', color: "var(--muted)", fontWeight: 600 }}>
		                Regiões e bairros
		                <textarea
		                  value={form.geography_regioes_text}
		                  onChange={e => setForm({ ...form, geography_regioes_text: e.target.value })}
		                  placeholder={"Zona Norte: Cidade Nova, Novo Aleixo\nZona Sul: Centro, Cachoeirinha"}
		                  rows={4}
		                  style={{ borderRadius: 8, border: "1px solid var(--border)", background: "var(--s2)", color: "var(--text)", padding: "10px 12px", resize: "vertical", font: "inherit", fontSize: '1rem' }}
		                />
		              </label>
		            </div>
            {submitError && <p role="alert" style={{ color: "var(--danger)", fontSize: '0.9375rem', lineHeight: 1.5 }}>{submitError}</p>}
            <div className="rf-form-actions">
              <FormCancel disabled={saving} />
              <Btn type="submit" disabled={saving} >
                {saving ? <Spinner /> : editItem ? "Salvar cliente" : "Criar cliente"}
              </Btn>
            </div>
          </Form>
        </Modal>
      )}

      <ConfirmModal
        open={Boolean(confirmId)}
        title="Desativar cliente"
        message="O cliente será desativado. Os usuários vinculados perderão acesso ao sistema."
        confirmLabel="Desativar"
        confirmVariant="danger"
        onClose={() => setConfirmId(null)}
        onConfirm={desativar}
      />

      {toast && !(modal && submitError) && (
        <Toast onDismiss={dismissToast} tone={toastType === "error" ? "error" : "success"}>{toast}</Toast>
      )}
    </div>
  );
}
